// src/app/error-page.tsx
// The app's single error SURFACE — a thin host over the Signal DS `ErrorPage` template.
//
// WHY THIS EXISTS. Before it, the PA mounted no error boundary and no router `errorElement` at all
// (measured 2026-09-08), so every thrown render or loader error fell through to React Router's
// built-in "Unexpected Application Error!" page with the raw stack — a clinician-facing stack trace.
//
// WHAT STAYS HERE. Everything drawn — the band, the kind colours, the disc / 404 hero, the copy table,
// focus-on-mount, the "Copied" confirmation — graduated into the DS `ErrorPage` (round 4d, verbatim
// from this file). The PA keeps only the app logic the DS deliberately does not own: the support
// PAYLOAD (kind, reference, the route reduced to its shape by `redactPath`, time, detail) and the
// clipboard write. The two wrappers (`route-error-element.tsx`, `app-error-boundary.tsx`) still do
// the classifying and supply the actions.
import { ErrorPage, ERROR_PAGE_KINDS, type ErrorPageKind, type ErrorPageProps } from '@scripsteam/scrips-signal-ds';

export type AppErrorKind = ErrorPageKind;

/** The copy table, one screen for the whole app's error voice — owned by the DS now. */
export const ERROR_KIND_SPECS = ERROR_PAGE_KINDS;

export type AppErrorPageProps = Omit<ErrorPageProps, 'onCopyDetails'>;

/** The support payload a clinician pastes into a ticket. Route SHAPE only — never a record id. */
export function supportPayload({
  kind,
  referenceId,
  details,
}: Pick<AppErrorPageProps, 'kind' | 'referenceId' | 'details'>): string {
  return [
    `kind: ${kind}`,
    `reference: ${referenceId ?? '(none)'}`,
    `route: ${redactPath(window.location.pathname)}`,
    `when: ${new Date().toISOString()}`,
    details ? `detail: ${details}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

export function AppErrorPage(props: AppErrorPageProps) {
  // The copy link exists only with a reference to copy. The clipboard is permission-gated and absent
  // over plain http; a rejection leaves the link as it was — the reference is on screen to read.
  const onCopyDetails = props.referenceId
    ? () => navigator.clipboard.writeText(supportPayload(props))
    : undefined;
  return <ErrorPage {...props} onCopyDetails={onCopyDetails} />;
}

/**
 * Reduce a pathname to route SHAPE before it goes anywhere a human might paste it.
 *
 * The PA's routes carry record identifiers — `/encounter/:appointmentId`,
 * `/patients/edit/:patientId`, `/billing/master-data/provider-fees/:providerId`. A record
 * number is an identifier under HIPAA, so the support payload gets the shape, not the id.
 * This is the same instinct as `observability/sentry.ts`'s `scrubUrl`, kept local and
 * deliberately narrower: that one is an allowlist because it feeds a third-party processor,
 * this one only has to survive being pasted into a support ticket.
 */
export function redactPath(pathname: string): string {
  return pathname
    .split('/')
    .map((seg) => {
      if (!seg) return seg;
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg);
      const isLongOpaque = /^[0-9]{4,}$/.test(seg) || /^[0-9a-z]{16,}$/i.test(seg);
      return isUuid || isLongOpaque ? ':id' : seg;
    })
    .join('/');
}
